import React, { useContext, useState } from "react";
import moment from "moment";
import { useMutation } from "@apollo/react-hooks";
import { useSelector } from "react-redux";
import gql from "graphql-tag";
import { MY_BOOKINGS } from "../routes";
import Progress from "../Common/progress";
import ActivityColor from "../Common/activityColor";
import BookButton from "../occasion/bookButton";
import SideInfo from "../Common/sideInfo";
import { makeStyles } from "@material-ui/styles";
import { Button } from "@mantine/core";
import Typography from "@material-ui/core/Typography";
import ListItem from "@material-ui/core/ListItem";
import LocationIcon from "@material-ui/icons/Place";
import PaymentIcon from "@material-ui/icons/Payment";
import DatesIcon from "@material-ui/icons/DateRange";
import OccasionsIcon from "@material-ui/icons/Event";
import { AppContext, OPEN_TOAST } from "../Context";

const useStyles = makeStyles((theme) => ({
  listItem: {
    border: `1px solid ${theme.app.iconDisabledColor}`,
    borderBottom: "none",
    background: theme.app.background[1],
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gridGap: "8px",
    alignItems: "center",
    width: "100%",
    padding: "0.5rem",
    "&:first-child": {
      borderRadius: "4px 4px 0 0",
    },
    "&:last-child": {
      borderRadius: "0 0 4px 4px",
      borderBottom: `1px solid ${theme.app.iconDisabledColor}`,
    },
    "&:only-child": {
      borderRadius: "4px",
      borderBottom: `1px solid ${theme.app.iconDisabledColor}`,
    },
    "@media(max-width: 1300px)": {
      "&:first-child": {
        borderRadius: 0,
      },
      "&:last-child": {
        borderRadius: 0,
        borderBottom: `1px solid ${theme.app.iconDisabledColor}`,
      },
      "&:only-child": {
        borderRadius: 0,
        borderBottom: `1px solid ${theme.app.iconDisabledColor}`,
      },
    },
    "@media(max-width: 600px)": {
      "&:first-child": {
        marginTop: "16px",
      },
      gridTemplateColumns: "1fr",
      marginBottom: "16px",
      borderBottom: `1px solid ${theme.app.iconDisabledColor}`,
    },
  },
  content: { padding: "8px" },
  infoContainer: {
    display: "grid",
    gridTemplateColumns: "auto auto auto 1fr",
    gridGap: "16px",
    alignItems: "center",
    margin: "8px 0 0",
    "@media(max-width: 600px)": {
      gridGap: "8px",
    },
  },
  title: {
    "@media(max-width: 600px)": {
      fontSize: "1.1rem",
    },
  },
  resource: {
    color: theme.app.labelColor,
    lineHeight: 1,
    fontSize: "0.875rem",
  },
  info: {},
  infoIcon: {
    fontSize: "20px",
    color: theme.app.labelColor,
  },
  infoItem: {
    display: "flex",
    alignItems: "center",
    marginBottom: "8px",
    color: theme.app.labelColor,
    "&:last-child": {
      marginBottom: 0,
    },
  },
  infoText: {
    color: theme.app.labelColor,
    fontSize: "0.8125rem",
    fontWeight: 500,
    "&:before": {
      content: '" "',
      whiteSpace: "pre",
    },
    "@media(max-width: 600px)": {
      fontSize: "0.75rem",
    },
  },
  reason: {
    fontStyle: "italic",
    color: theme.app.dangerColor,
    lineHeight: 1.6,
    fontSize: "0.875rem",
  },
  actionContainer: {
    height: "100%",
    display: "flex",
    alignItems: "flex-end",
    padding: "8px",
    "@media(max-width: 600px)": {
      padding: "4px 0",
      // borderTop: `1px solid ${theme.app.iconDisabledColor}`,
      justifyContent: "flex-end",
    },
  },
  actionButton: {
    marginRight: "8px",
    color: theme.app.labelColor,
    "@media(max-width: 600px)": {
      marginRight: 0,
      border: "none",
    },
  },
  actionButtonText: {},
}));

const BOOK_COURSE = gql`
  mutation reserveActivity($id: String!, $participantId: Guid) {
    reserveActivity(activityId: $id, participantId: $participantId) {
      position
      failed
      errorMessage
    }
  }
`;

const Course = React.memo(function Course(props) {
  const { course, manages } = props;
  const { activity } = course;
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const context = useContext(AppContext);
  const { dispatch } = context;
  const { language, activePersonId } = useSelector((state) => ({
    language: state.language,
    activePersonId: state.activePersonId,
  }));
  const handleShowInfo = () => setOpen(true);
  const handleCloseInfo = () => setOpen(false);

  const handleMutationError = (error) =>
    dispatch({
      type: OPEN_TOAST,
      toastText: language.errorMsg,
      toastType: "error",
    });
  const handleMutationSuccess = ({ reserveActivity }) => {
    const { failed, errorMessage } = reserveActivity;
    if (failed) {
      return dispatch({
        type: OPEN_TOAST,
        toastText: errorMessage,
        toastType: "error",
      });
    }
    dispatch({
      type: OPEN_TOAST,
      toastText: language.bookingSucceed,
      toastType: "success",
    });
  };
  const [reserveActivity, { loading, error }] = useMutation(BOOK_COURSE, {
    variables: { id: course.id, participantId: activePersonId },
    onError: handleMutationError,
    onCompleted: handleMutationSuccess,
    refetchQueries: () => [{ query: MY_BOOKINGS }],
  });

  if (!activity) {
    return <h3>Error</h3>;
  }
  const occasion = (activity.occasions && activity.occasions[0]) || {};
  const blocked = !course.canReserve.reserve && !course.canReserve.enqueue;
  const reason =
    language.reason[course.canReserve.reason] || course.canReserve.reason;

  if (!occasion) {
    return <h3>Error</h3>;
  }
  const price = activity.price;
  const booked = Math.round(
    (course.totalBooked / course.maxParticipants) * 100
  );

  const anyResource = activity.resourceAssignments.length > 0;
  const resource = anyResource && activity.resourceAssignments[0].resource;
  const firstOccasion = moment(course.firstOccasion).format("YYYY-MM-DD");
  const lastOccasion = moment(course.lastOccasion).format("YYYY-MM-DD");

  return (
    <>
      <ListItem className={classes.listItem}>
        <div className={classes.content}>
          <Typography variant="h2" className={classes.title}>
            {activity.name}
          </Typography>
          {resource.name && (
            <Typography className={classes.resource}>
              {resource.name}
            </Typography>
          )}
          {blocked && (
            <Typography className={classes.reason} component="div">
              {reason}
            </Typography>
          )}
          <div className={classes.infoContainer}>
            <ActivityColor color={activity.color} />
            <Progress booked={booked} />
            <div className={classes.info}>
              <Typography component="div" className={classes.infoItem}>
                <LocationIcon className={classes.infoIcon} />
                <div className={classes.infoText}>{activity.location.name}</div>
              </Typography>
              <Typography component="div" className={classes.infoItem}>
                <PaymentIcon className={classes.infoIcon} />
                <div className={classes.infoText}>
                  {price.inclVat > 0 ? `${price.inclVat}:-` : `-`}
                </div>
              </Typography>
            </div>
            <div className={classes.info}>
              <Typography component="div" className={classes.infoItem}>
                <DatesIcon className={classes.infoIcon} />
                <div
                  className={classes.infoText}
                >{`${firstOccasion} - ${lastOccasion}`}</div>
              </Typography>
              <Typography component="div" className={classes.infoItem}>
                <OccasionsIcon className={classes.infoIcon} />
                <div className={classes.infoText}>
                  {`${activity.occasions && activity.occasions.length} ${
                    language.occasions
                  }`}
                </div>
              </Typography>
            </div>
          </div>
        </div>
        <div className={classes.actionContainer}>
          <Button
            size="sm"
            color="violet.8"
            variant="outline"
            className={classes.actionButton}
            onClick={handleShowInfo}
            aria-label={`${language.infoAboutCourse} ${activity.name}`}
          >
            <div className={classes.actionButtonText}>{language.showInfo}</div>
          </Button>
          {!blocked && (
            <BookButton
              bookingData={course}
              book={reserveActivity}
              manages={manages}
              loading={loading}
              error={error}
            />
          )}
        </div>
      </ListItem>
      <SideInfo open={open} onClose={handleCloseInfo} activity={activity} />
    </>
  );
});

export default Course;
